import React, {useMemo, useState} from "react"
import {Navigate, Outlet, useParams} from "react-router-dom"
import {ThemeProvider} from "@mui/material"
import {useConfig} from "../hooks/useConfig"
import {useParticipant} from "../hooks/useParticipant"
import {createMuiTheme} from "../library/MuiTheme"
import Loading from "./common/Loading"
import PageNotFound from "./views/PageNotFound"
import ClosedModal from "./modals/ClosedModal"
import InternetExplorerModal from "./modals/InternetExplorerModal"
import AgeGateModal from "./modals/AgeGateModal"
import HeaderImage from "./common/HeaderImage"
import * as DeviceUtil from "../utils/DeviceUtil"
import Cookies from 'universal-cookie'

function BoothLayout() {
  const params = useParams<"code" | "step" | "uploadCode" | "picPresentation">()
  const code = params.code!

  const {config, error, loading} = useConfig(code)
  const {participant} = useParticipant(config?.presetCode ?? code)

  const [checkedAge, setCheckedAge] = useState(() => {
    const failedAgeGate = new Cookies().get("failedAgeGate")
    return Boolean(typeof failedAgeGate !== "undefined")
  })
  const [failedAgeGate, setFailedAgeGate] = useState(() => {
    const failedAgeGate = new Cookies().get("failedAgeGate")
    return Boolean(failedAgeGate === "true")
  })
  const [isInternetExplorer] = useState(() => DeviceUtil.isInternetExplorer())
  const muiTheme = useMemo(() => config ? createMuiTheme(config.theme) : null, [config])

  const passAgeGate = (didPass: boolean) => {
    setCheckedAge(true)
    setFailedAgeGate(!didPass)

    if (didPass) {
      new Cookies().set("failedAgeGate", !didPass)
    }
  }

  if (error) {
    return <PageNotFound error={error}/>
  }

  if (loading || !config) {
    return (
      <div className="verticalCenter" style={{textAlign: "center"}}>
        <Loading/>
      </div>
    )
  }

  const lockOutForAge = config.ageGate.enabled && failedAgeGate
  const theme = config.theme
  const step = params.step ?? null

  // Redirect to splash if participant doesn't have required data (except for splash and pic pages)
  const requiresParticipant = step !== null && step !== "pic"
  if (requiresParticipant && !participant.hasExpectedData(config.dataCapture)) {
    return <Navigate to={`/${config.presetCode}`} replace />
  }

  const headerImageComponent = <HeaderImage image={theme.headerImage} theme={theme}/>

  return (
    <ThemeProvider theme={muiTheme!}>
      <main className="background text-center" style={theme.backgroundStyle}>
        <div className="background pin" style={theme.backgroundImageStyle}></div>
        <div className="pin scroll">
          {theme.headerImageUrl ?
            <a href={theme.headerImageUrl} target="_blank" rel="noopener noreferrer">
              {headerImageComponent}
            </a>
            : headerImageComponent
          }
          <Outlet/>

          {isInternetExplorer ?
            <InternetExplorerModal show/>
            :
            <>
              <ClosedModal show={config.closed || lockOutForAge} gallery={config.gallery}/>
              <AgeGateModal
                prompt={config.ageGate.prompt}
                minimumAge={config.ageGate.minimumAge}
                show={config.ageGate.enabled && !checkedAge && step !== "pic"}
                passAgeGate={(didPass: boolean) => passAgeGate(didPass)}
              />
            </>
          }
        </div>
      </main>
    </ThemeProvider>
  )
}

export default BoothLayout
